import { type SxProps, type Theme } from '@mui/material';

const cardChromeSx = ({ backrs }: Theme) => ({
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  boxSizing: 'border-box' as const,
});

export const cardSx: SxProps<Theme> = (theme) => ({
  ...cardChromeSx(theme),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.backrs.spacing['spacing-2xl'],
  width: '100%',
  height: '100%',
  padding: theme.backrs.spacing['spacing-2xl'],
});

export const metricCardSx: SxProps<Theme> = (theme) => ({
  ...cardChromeSx(theme),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.backrs.spacing['spacing-md'],
  width: '100%',
  minWidth: 0,
  padding: theme.backrs.spacing['spacing-2xl'],
});

export const tallCardSx: SxProps<Theme> = (theme) => ({
  ...cardChromeSx(theme),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.backrs.spacing['spacing-2xl'],
  alignSelf: 'stretch',
  minHeight: 298,
  width: '100%',
  padding: `${theme.backrs.spacing['spacing-3xl']} ${theme.backrs.spacing['spacing-2xl']}`,
});

const clickableCardSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

// MUI's sx typing rejects an array of SxProps, so the composed pair needs the cast.
export const withClickable = (
  base: SxProps<Theme>,
  onClick: (() => void) | undefined,
): SxProps<Theme> => (onClick ? ([base, clickableCardSx] as SxProps<Theme>) : base);

export const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

export const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

export const mutedTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

export const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

export const rowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

export const legendDotSx: SxProps<Theme> = ({ backrs }) => ({
  width: 10,
  height: 10,
  borderRadius: backrs.radius['radius-full'],
  flexShrink: 0,
});

export const pctChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

export const pctChipTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

export const countChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 40,
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  flexShrink: 0,
});

export const pieRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

export const pieLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

export const donutWrapSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
